import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { useIsMobile } from '../../hooks/useIsMobile';

/** Where a phone goes instead of a menu editor. */
export const TODAYS_MENU_ROUTE = '/menucycle/today';

/** The line for menu analysis, which edits nothing (Android review MN-7). */
export const MENU_ANALYSIS_PHONE_LINE = 'Menu analysis needs a wider screen. Open it on a tablet or computer.';

/**
 * A menu editing page, closed on a phone (useIsMobile's isPhone: 768px or narrower,
 * or a touch screen whose short side is 500px or less, so a phone held sideways too).
 * The editors need a wide screen and a long one; at 390px tall the workbench grid
 * shows two rows. On a phone this is one plain line with today's menu (read-only)
 * and Back; on a tablet or computer it is the page itself. A page that edits nothing
 * passes its own `message`, so the line does not say "Menu editing".
 */
export default function DesktopOrTabletOnly({ children, message }: { children: ReactNode; message?: string }) {
  const { isPhone } = useIsMobile();
  const navigate = useNavigate();

  if (!isPhone) return <>{children}</>;

  // Opened from a link there is somewhere to go back to; typed or bookmarked, the list.
  const back = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/menucycle');
  };

  return (
    <div
      role="status"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, padding: 16 }}
    >
      <p style={{ margin: 0, color: 'var(--text-primary)' }}>{message ?? 'Menu editing needs a tablet or computer.'}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button variant="contained" size="small" onClick={() => navigate(TODAYS_MENU_ROUTE)} sx={{ textTransform: 'none' }}>
          View today's menu
        </Button>
        <Button variant="outlined" size="small" onClick={back} sx={{ textTransform: 'none' }}>
          Back
        </Button>
      </div>
    </div>
  );
}
